import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DatePicker, Form, Input, Modal, Select, Spin } from 'antd'
import dayjs from 'dayjs'

import {
    addProject,
    browseProjectsListForClient,
    projectsListForClientModal,
    resetClientModalProjects,
    resetBrowseProjectsList,
    projectsState,
    updateProject,
} from '../projects.slice'
import { loginState } from '../../login/login.slice'
import { DEFAULT_COLOR_PALETTE } from '../../../constants/projects'

import styles from './CreateProjectModal.module.css'
import { showActionNotification } from '../../../components/notification'

const { TextArea } = Input
const { RangePicker } = DatePicker

const PROJECT_TYPE_OPTIONS = [
    { label: 'Fixed Cost', value: 'Fixed Cost' },
    { label: 'Dedicated Resource', value: 'Dedicated Resource' },
    { label: 'Hourly Basis', value: 'Hourly Basis' },
]

const getProjectColor = (projectName) => {
    const colors = DEFAULT_COLOR_PALETTE
    if (!projectName) return colors[0]

    let hash = 0
    for (let i = 0; i < projectName.length; i++) {
        hash = projectName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
}

const normalizeProjectKey = (value) => {
    if (typeof value !== 'string') return value
    return value.toUpperCase().replace(/[^A-Z0-9_]/g, '')
}

const CreateProjectModal = ({
    open,
    onCancel,
    listMode = 'clientModal',
    mode = 'create', // 'create' | 'edit'
    projectId,
    initialProject,
    onSaved,
}) => {
    const dispatch = useDispatch()
    const [form] = Form.useForm()
    const { userProfile } = useSelector(loginState)
    const { clientModalProjectSearch } = useSelector(projectsState)

    const [loading, setLoading] = useState(false)
    const [colorTouched, setColorTouched] = useState(false)

    const nameValue = Form.useWatch('name', form)
    const computedColor = useMemo(() => getProjectColor(nameValue), [nameValue])

    useEffect(() => {
        if (!open) return
        form.resetFields()
        setColorTouched(false)
        if (mode === 'edit' && initialProject) {
            const rawStart = initialProject?.start ?? null
            const rawEnd = initialProject?.end ?? null
            const startV =
                rawStart != null && rawStart !== '' ? dayjs(rawStart) : null
            const endV = rawEnd != null && rawEnd !== '' ? dayjs(rawEnd) : null

            form.setFieldsValue({
                name: initialProject?.name ?? '',
                projectType: initialProject?.projectType ?? undefined,
                projectKey: initialProject?.projectKey ?? '',
                dateRange: startV || endV ? [startV, endV] : null,
                description: initialProject?.description ?? '',
                projectColor:
                    initialProject?.colorCode ||
                    initialProject?.projectColor ||
                    getProjectColor(initialProject?.name || ''),
            })
        } else {
            form.setFieldsValue({
                projectColor: getProjectColor(''),
            })
        }
    }, [open, mode, initialProject, form])

    useEffect(() => {
        if (!open) return
        if (mode === 'edit') return
        if (colorTouched) return
        form.setFieldsValue({ projectColor: computedColor })
    }, [open, mode, colorTouched, computedColor, form])

    const handleSubmit = async (values) => {
        setLoading(true)
        try {
            const range = values.dateRange
            const startD = Array.isArray(range) ? range[0] : null
            const endD = Array.isArray(range) ? range[1] : null
            const resolvedColor =
                values?.projectColor ||
                (mode === 'edit'
                    ? initialProject?.colorCode || initialProject?.projectColor
                    : null) ||
                computedColor ||
                getProjectColor(values?.name?.trim?.() || '')
            const common = {
                name: values.name?.trim(),
                projectType: values.projectType,
                projectKey: values.projectKey?.trim(),
                start: startD ? startD.valueOf() : null,
                end: endD ? endD.valueOf() : null,
                description: values.description?.trim() || '',
                // Backend expects both fields in some flows; keep them in sync.
                colorCode: resolvedColor,
                projectColor: resolvedColor,
            }

            const result =
                mode === 'edit'
                    ? await dispatch(
                          updateProject({
                              _id: projectId || initialProject?._id,
                              updatedData: common,
                          })
                      )
                    : await dispatch(
                          addProject({
                              ...common,
                              projectLead: userProfile?._id,
                          })
                      )
            const data = result?.payload?.data

            if (data?.success) {
                showActionNotification({
                    message:
                        mode === 'edit'
                            ? 'Project updated successfully'
                            : 'Project created successfully',
                    subMessage:
                        mode === 'edit' ? initialProject?.name : values.name,
                    actionButtonText: 'Undo',
                    notificationKey:
                        mode === 'edit' ? 'project-updated' : 'project-created',
                })
                form.resetFields()
                setColorTouched(false)
                onCancel?.()
                onSaved?.()

                // Refresh only the relevant list, to avoid cross-effects between
                // sidebar projects list and the "Browse projects" page.
                if (mode !== 'edit' && listMode === 'browseProjects') {
                    dispatch(resetBrowseProjectsList())
                    dispatch(
                        browseProjectsListForClient({
                            skip: 0,
                            filter: {},
                            limit: 50,
                            search: '',
                            userProfile,
                        })
                    )
                } else if (mode !== 'edit') {
                    dispatch(resetClientModalProjects())
                    dispatch(
                        projectsListForClientModal({
                            skip: 0,
                            filter: clientModalProjectSearch
                                ? { search: clientModalProjectSearch }
                                : {},
                            limit: 10,
                            search: clientModalProjectSearch || '',
                            userProfile,
                        })
                    )
                }
            } else {
                const msg =
                    data?.message ||
                    (mode === 'edit'
                        ? 'Failed to update project. Please try again.'
                        : 'Failed to create project. Please try again.')
                if (typeof msg === 'object' && msg) {
                    Object.values(msg).forEach((m) =>
                        showActionNotification({
                            type: 'error',
                            message: 'Something went wrong',
                            subMessage: String(m),
                            notificationKey: 'project-save-error',
                        })
                    )
                } else {
                    showActionNotification({
                        type: 'error',
                        message: 'Something went wrong',
                        subMessage: String(msg),
                        notificationKey: 'project-save-error',
                    })
                }
            }
        } catch (e) {
            showActionNotification({
                type: 'error',
                message: 'Something went wrong',
                subMessage: 'An error occurred. Please try again.',
                notificationKey: 'project-save-error',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        setColorTouched(false)
        onCancel?.()
    }

    return (
        <Modal
            title={
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderLeft}>
                        <div className={styles.modalHeaderTitle}>
                            {mode === 'edit'
                                ? 'Edit Project'
                                : 'Create Project'}
                        </div>
                    </div>
                </div>
            }
            open={open}
            onCancel={handleCancel}
            footer={
                <div className={styles.modalFooter}>
                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={() => {
                                if (!loading) form.submit()
                            }}
                            className={`${styles.submitButton} ${
                                loading ? styles.submitButtonDisabled : ''
                            }`}
                        >
                            {loading
                                ? mode === 'edit'
                                    ? 'Saving...'
                                    : 'Creating...'
                                : mode === 'edit'
                                  ? 'Save changes'
                                  : 'Create Project'}
                        </button>
                    </div>
                </div>
            }
            width={550}
            className="create-project-modal"
            destroyOnHidden
            centered
            closeIcon={null}
        >
            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark
                    className={`create-project-form ${styles.form}`}
                    initialValues={{
                        dateRange: null,
                    }}
                >
                    <Form.Item name="projectColor" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Project Name"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter project name',
                            },
                            { min: 2, message: 'At least 2 characters' },
                        ]}
                    >
                        <Input
                            placeholder="e.g. Festival Support Portal"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Project Type"
                        name="projectType"
                        rules={[
                            {
                                required: true,
                                message: 'Please select project type',
                            },
                        ]}
                    >
                        <Select
                            size="large"
                            placeholder="Select type"
                            options={PROJECT_TYPE_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Project Key"
                        name="projectKey"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter project key',
                            },
                            {
                                pattern: /^[A-Z0-9_]{2,12}$/,
                                message: 'Use 2–12 chars: A–Z, 0–9, underscore',
                            },
                        ]}
                        normalize={normalizeProjectKey}
                    >
                        <Input
                            placeholder="e.g. SUPPORT"
                            size="large"
                            maxLength={12}
                        />
                    </Form.Item>

                    <Form.Item label="Start & End Date" name="dateRange">
                        <RangePicker
                            size="large"
                            style={{ width: '100%' }}
                            placeholder={['Start date', 'End date']}
                            format="D MMM YYYY"
                            getPopupContainer={() => document.body}
                            disabledDate={(current, { from }) => {
                                if (!current) return false
                                if (from) {
                                    return current.isBefore(from, 'day')
                                }
                                return false
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Description" name="description">
                        <TextArea
                            placeholder="Add a short description (optional)"
                            autoSize={{ minRows: 3, maxRows: 6 }}
                        />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    )
}

export default CreateProjectModal
